import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable()
export class BackupApiInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const requestUrl = req.url;
    const baseUrl = environment.baseUrl;
    const tenantUrl = environment.tenantApi;

    // Define a helper function to determine the appropriate backup URL
    const getBackupUrl = (url: string): string => {
      if (url.startsWith(baseUrl)) {
        return environment.backupBaseUrl + url.substr(baseUrl.length);
      } else if (url.startsWith(tenantUrl)) {
        return environment.backupTenantApi + url.substr(tenantUrl.length);
      }
      return url; // Default case, should not be hit if properly configured
    };

    if (requestUrl.includes('stripe/subscription/create')) {
      return next.handle(req);
    } else if (
      requestUrl.startsWith(baseUrl) ||
      requestUrl.startsWith(tenantUrl)
    ) {
      return next.handle(req).pipe(
        catchError((error: any) => {
          // Check if the error is an HttpErrorResponse, indicating a failed HTTP call
          if (error instanceof HttpErrorResponse) {
            // A backup transport failure must never replace a definitive
            // rejection of the bearer by the primary API. Propagate the
            // primary response directly so ServerErrorInterceptor can expire
            // the exact session that authenticated this request.
            if (this.isDefinitiveAuthenticationFailure(req, error)) {
              return throwError(() => this.wrapHttpError(error));
            }

            // Switch to the appropriate backup URL based on the request URL
            const backupUrl = getBackupUrl(requestUrl);

            // After the endpoint switch, main and backup can resolve to the
            // SAME URL (prod config): re-sending the identical request adds
            // no failover value and only doubles the load on the already
            // failing server (a duplicate POST per failure, times the
            // component-level retries). Skip the backup re-send and
            // propagate the original failure with the exact wrapped shape
            // (server detail message + HTTP status) callers rely on. When
            // the configured backup URL actually differs, the failover
            // below is unchanged.
            if (backupUrl === requestUrl) {
              console.error('Backup API skipped (backup URL identical to primary): ' + JSON.stringify({
                requestUrl,
                status: error.status,
                message: error.message,
                error: error.error,
              }));
              return throwError(() => this.wrapHttpError(error));
            }

            const backupRequest = req.clone({ url: backupUrl });

            // Handle the backup request
            return next.handle(backupRequest).pipe(
              catchError((backupError: HttpErrorResponse) => {
                // Handle backup API error here
                console.error('Backup API failed: ' + JSON.stringify({
                  requestUrl,
                  backupUrl,
                  status: backupError.status,
                  message: backupError.message,
                  error: backupError.error,
                }));

                return throwError(() => this.wrapHttpError(backupError));
              })
            );
          }
          // If not an HttpErrorResponse, propagate the original error
          return throwError(() => error);
        })
      );
    } else {
      // If request URL does not start with base URL or tenant URL, proceed without backup
      return next.handle(req);
    }
  }

  private isDefinitiveAuthenticationFailure(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
  ): boolean {
    if (!request.headers.get('Authorization')) {
      return false;
    }

    const explicitUnauthorized =
      error.status === 401 || error.error === 'Unauthorized';
    const legacySessionTimeout =
      error.status === 500 &&
      error.error?.details?.[0] === 'No Logged In User Found';
    const profileForbidden =
      error.status === 403 &&
      /\/user\/profile(?:\?|$)/.test(request.url);

    return (
      explicitUnauthorized || legacySessionTimeout || profileForbidden
    );
  }

  /**
   * Preserve the backend error detail (ExceptionResponse.details[0]) so the
   * user sees the real failure reason, not a generic text, and keep the HTTP
   * status on the wrapped error so callers can distinguish transient
   * failures (0/5xx) from 4xx rejections. Applied identically to a failed
   * backup attempt and to a primary failure whose backup re-send was skipped
   * (identical URL), so callers always observe the same error shape.
   */
  private wrapHttpError(httpError: HttpErrorResponse): Error {
    const serverDetail =
      httpError?.error?.details?.[0] ||
      httpError?.error?.message ||
      (typeof httpError?.error === 'string' ? httpError.error : undefined);
    const errorMessage =
      typeof serverDetail === 'string' && serverDetail.trim()
        ? serverDetail
        : httpError.message || 'Backup API failed';
    const wrappedError = new Error(errorMessage);
    (wrappedError as any).status = httpError.status;
    return wrappedError;
  }
}
